import { CompanyCompetencies } from "./CompanyCompetencies";
import { ComparisonSelection } from "@/components/comparison/ComparisonSelection";
import { ComparisonTools } from "@/components/comparison/ComparisonTools";
import { companyComparable } from "./company-comparable";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useParams } from "react-router";
import { ShareItemCard } from "@/components/share/ShareItemCard";
import { AvatarCropper } from "@/components/common/AvatarCropper";
import { BackButton } from "@/components/common/BackButton";
import { NoBackend } from "@/components/common/NoBackend";
import { UnsavedChangesDialog } from "@/components/common/UnsavedChangesDialog";
import { Button } from "@/components/design/Button";
import { InlineEdit } from "@/components/design/InlineEdit";
import { Section } from "@/components/design/Section";
import { usePendingEdits, usePendingScope } from "@/lib/pending-edits";
import { useUnsavedEditsGate } from "@/lib/unsaved-edits-gate";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { localizedMessageOf } from "@/lib/localized-error";
import { logApplicationFailure, userErrorMessage } from "@/lib/user-error";
import { useMe } from "@/auth/use-me";
import { OrganizationalRecordSection } from "@/pages/organizational-record/OrganizationalRecordSection";
import { OrganizationalRecordToggle } from "@/pages/organizational-record/OrganizationalRecordToggle";
import { useOrganizationalRecordAccess } from "@/pages/organizational-record/use-organizational-record-access";
import { useOrganizationalRecordSection } from "@/pages/organizational-record/use-organizational-record-section";
import { COMPANY_ROLE_ADMIN } from "./company-roles";
import { EMPTY_COMPANY_DRAFT } from "./company-profile-draft";
import { CompanyCompetencyEditor } from "./CompanyCompetencyEditor";
import { CompanyIdentity } from "./CompanyIdentity";
import { CompanyMemberCount } from "./CompanyMemberCount";
import { CompanyMemberEditor } from "./CompanyMemberEditor";
import { CompanyPersonPill } from "./CompanyPersonPill";
import { CompanyStatusControl } from "./CompanyStatusControl";
import {
  COMPANY_PENDING_SCOPES,
  COMPANY_SCOPE_LOGO,
  COMPANY_SCOPE_PROFILE,
} from "./pending-scopes";
import { useCompany } from "./use-company";
import { useCompanyAdmin } from "./use-company-admin";
import {
  useCompanyCreation,
  type CompanyCreateSource,
} from "./use-company-creation";
import { useCompanyContext } from "./use-company-context";
import {
  useCompanyLogoEditing,
  type CompanyLogoEditing,
} from "./use-company-logo-editing";
import {
  useCompanyProfileEditing,
  type CompanyProfileEditing,
} from "./use-company-profile-editing";

// The company page at /c/:id (spark#221, the company page).
//
// A showcase, not a match cockpit. On it: identity (monogram + name + industry), size, the
// "About us" free text, the member strip and the contact persons. Deliberately NOT on it, per the
// ADR: Powers, Matches, a tagline, or a product field. Do not add them here without amending it.
//
// THE MEMBER STRIP IS PAGED AND THE CONTACT STRIP IS NOT (@external-adr GENESIS-0029-the-member-strip-is-paged-and-the-contact-strip-is-not). §2 asks for a compact
// avatar row with an "Anzahl", so the strip shows one page, states how many there are, and offers
// one control for the next page. No infinite scroll: a showcase page is not a feed, and a reader who
// only wants to know who works here pays for one page.
//
// THE STRIP DRAWS FACES. It drew monograms for a while, on the rule that an avatar travels base64
// inside the response and a list whose length the CALLER did not settle would therefore ship one
// image per row and grow with the company's headcount. That rule is right and this list is not the
// shape it describes: the roster is paged and spark settles the page at MEMBER_PAGE_SIZE, which is
// the very test that admits the logo on `myCompanies` and `companyIdentities` — the number of images
// is decided before the request goes out. What the monograms cost was visible on the page: the same
// person appeared twice, photographed in the contact block and reduced to initials in the strip.
//
// CONTACT PERSONS (§4) ARE THEIR OWN READ AND THEIR OWN BLOCK. §4 makes them referenced members with
// no free-text e-mail or phone field, capped per company (genesis#268 raised that cap from the three
// §4 wrote to ten, which is the only thing about §4 that changed), so each one is a link to a real
// profile and there is nothing else to show. They are no longer the marked rows of the strip — that
// filter answered "the contacts on page one" the moment the roster was paged — but they are still the
// SAME predicate as the roster, because genesis serves both from one (see use-company). What that
// predicate carries changed with concept v5: the per-caller @external-adr GENESIS-0023-company-membership-is-the-per-company-authorization §7 publication rule is gone, so
// the roster and the contact strip are now the same for every caller.
// When none are marked the block renders NOTHING — no heading and no empty frame, since a frame
// promising contacts a company has not named is worse than its absence.
//
// CONTACTS COME FIRST, WHICH THE CONCEPT DOES NOT SAY. the company page lists §2 members before §4
// contacts; @adr SPARK-0039-contact-persons-come-before-the-roster@2 inverts it, deliberately and with Kevin's authority, because the two lists are
// not peers: the contact block is capped and every entry is an action, while the roster is unbounded,
// paged at twelve, and is context. Neither is visually louder — both are plain sections, and the
// contact block is distinguished by shape and a sentence rather than by weight or colour.
//
// TWO WAYS IN, AND BOTH STAY. The admin affordances are INLINE on this page — the route already
// answers "which company am I acting on" — and the header additionally carries the
// "Kontext-Switch-Button" the company page puts there for admins of the company. The switch is an
// ADDITION, not a replacement: it makes the company the ambient context across the app, while the
// inline editing remains how the editing itself is done. Both cover exactly what the company account model
// permits — view and edit the company, manage its members — and nothing beyond it. genesis
// @external-adr GENESIS-0025-no-company-context-switch-in-the-mvp, which had declined the switch, is
// superseded; see its header for the reversal.
//
// EDITING IS IN PLACE, AND THERE IS NOWHERE ELSE TO DO IT. Turning the mode on used to APPEND two
// sections to the bottom of the page — "Company logo" and "Company profile" — which redrew the
// identity this page was already showing as a stack of form fields. The same company name was on
// screen twice, the same industry twice, the same size twice and the same "About us" text twice,
// and nothing said which copy was the live one. Both sections are gone. What turns on now is the
// values themselves: the logo becomes the upload control, the name, the industry and the size
// become fields in the header, and the About us box becomes its own text area.
//
// AN UNSET FIELD IS ABSENT TO A READER AND REACHABLE TO AN ADMIN. Those are two jobs and this page
// used to confuse them — the header printed "Industry not stated" to every passing visitor, while
// an admin who wanted to set it had to find the duplicate field at the bottom of the page. A
// visitor now sees only what the company actually stated; an admin sees a placeholder standing
// exactly where the value will be, and the placeholder is the control.
//
// WHAT STAYS EXPLICIT. Removing the logo, which deletes what genesis already holds and is a two-step
// (@adr SPARK-0011-destructive-actions-require-confirmation@1) rather than a draft; and deactivating the company, which 0006's v7 change note calls a lifecycle
// state rather than a Steckbriefwert and which therefore never belongs in the profile draft.
//
// Expertise and people are NO LONGER in that list. Both used to commit one row per click, and this
// comment said the SaveBar could not express them — genesis#263 staged both, so today each registers
// a pending scope (COMPANY_SCOPE_COMPETENCY, COMPANY_SCOPE_MEMBER) and the bar writes them with
// everything else.

/**
 * @param creating renders the SAME page for a company that does not exist yet (@adr SPARK-0038-creating-a-company-is-the-company-page-before-it-exists@2). It is not
 *   a second layout and not a second set of components — it is this page before genesis has an id
 *   for what is on it. `/c/new` is the only caller.
 *
 *   Taken as `registering` because the body needs a second, narrower word: `creating` below is
 *   the STATE — true only while the created company has not been read back — and it is what
 *   everything downstream keys on. The prop is what the router said; `creating` is what is true
 *   right now.
 */
export function CompanyPage({
  creating: registering = false,
}: { creating?: boolean } = {}) {
  const intl = useIntl();
  const me = useMe();
  const { id: routeId = "" } = useParams();
  // WHAT THE PAGE IS ABOUT. On /c/:id that is the route's id and nothing changes it. On /c/new it is
  // empty until the create gate answers, and from that moment on it is the company genesis minted —
  // which is why the id is held here rather than read from the URL: the reads have to attach to the
  // new company BEFORE the URL moves, or the profile draft would never see a stored value to settle
  // against and the page would stay permanently "unsaved".
  const [createdId, setCreatedId] = useState<string | null>(null);
  const id = createdId ?? routeId;
  const {
    company,
    members,
    rosterWithheld,
    memberCount,
    hasMoreMembers,
    loadMoreMembers,
    loadingMoreMembers,
    moreMembersError,
    contactPersons,
    sharedItems,
    competencies,
    viewerIsAdmin,
    loading,
    notFound,
    error,
  } = useCompany(id);
  // THE CREATE STATE ENDS WHEN THE COMPANY HAS BEEN READ BACK, not when genesis answers the
  // mutation. Between those two moments the company exists but this page has never seen it: the
  // profile draft still has an empty stored value to compare against, so it would report itself
  // dirty, raise the SaveBar over a successful create and block the navigation that ends the state.
  // Waiting for the read closes that window without a single line of save-machinery change.
  const creating = registering && !company;
  const draftSource = useRef<CompanyCreateSource>({
    profile: EMPTY_COMPANY_DRAFT,
    logo: null,
  });
  const creation = useCompanyCreation(draftSource);
  // The stored name rides along on every write: UpdateCompanyDataInput.name is String!, so even a
  // logo-only update has to carry it. "" only while the query is still in flight, and no control —
  // and therefore no mutation — is on screen before the company has loaded.
  const admin = useCompanyAdmin(
    id,
    company?.name ?? "",
    creating ? creation.ensure : undefined,
  );
  // The acting-company context is READ here and never consulted by a write: `admin` above targets
  // the route's company id whatever the ambient context says, which is what keeps the switch from
  // becoming the thing that authorizes.
  const companyContext = useCompanyContext();
  const actingAsThisCompany = companyContext.active?.id === id;
  // THE ORGANISATION'S FILE ABOUT THIS COMPANY is reached by capability alone (@external-adr GENESIS-0035-organizational-records-are-a-global-capability):
  // whoever may read records sees the button and the section, admin or not, and a company admin
  // without that capability sees neither. Nothing of it on /c/new, where there is no company to keep
  // a file about yet, not even the access check; the `registering` prop rather than `creating`,
  // because the record's anchor navigation rewrites the URL and would put /c/new back into the
  // address bar that the create flow just corrected to /c/:id.
  const organizationalRecordAccess = useOrganizationalRecordAccess({ skip: registering });
  const organizationalRecordSubjectId = registering ? undefined : company?.id;
  const { expanded: organizationalRecordExpanded, sectionRef: organizationalRecordSectionRef, open: openOrganizationalRecord } =
    useOrganizationalRecordSection(organizationalRecordSubjectId, organizationalRecordAccess.canRead);
  // What the assistant may know about this screen (@external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption):
  // the company on it, by name, and the viewer's standing — never the id, which travels as the
  // binding context (agentBindingContextForPath) so the model cannot choose another company.
  useAssistantContext(
    company
      ? [
          {
            description: "the company page the user is looking at",
            value:
              `${company.name}` +
              (company.industry?.name ? `, ${company.industry.name}` : "") +
              (company.employeeCount != null ? `, ${company.employeeCount} employees` : "") +
              (viewerIsAdmin ? "; the user administers it" : "; the user is viewing it"),
          },
        ]
      : null,
  );
  // ENTERING THE CONTEXT DESTROYS AN OPEN EDIT UNLESS IT ASKS. The switch resets the Apollo cache
  // (@adr SPARK-0034-acting-company-context-resets-the-apollo-cache@3) and the page falls back to its loading state while the reads come back, which takes
  // the controls and their registry entries off screen. The URL does not move, so UnsavedGuard's
  // router blocker never sees it — the same PendingEdits registry is consulted directly instead.
  const contextGate = useUnsavedEditsGate();
  // A SECOND GATE, for leaving edit mode. Same machinery, different occasion and therefore different
  // words — one instance holds one action at a time, and sharing it would make "Done editing" and
  // "Act as this company" able to cancel each other. Leaving edit mode is exactly the case the gate
  // was built for: it destroys unsaved work without the URL moving, so no router blocker fires.
  const editingGate = useUnsavedEditsGate();
  const [editing, setEditing] = useState(false);
  // `viewerIsAdmin` is derived from the membership row and is presentation only — genesis re-checks
  // it on every mutation. ANDing it in here rather than trusting `editing` alone means a viewer who
  // loses the row mid-session stops seeing the controls on the next render.
  // `createdId` keeps the mode on across the gap between the create and the first read, where
  // `viewerIsAdmin` is still false because MyCompanyRoles has not come back. Without it the editors
  // would unmount for a render or two — and unmounting an editor is what throws its draft away.
  const isEditing =
    creating || createdId !== null || (viewerIsAdmin && editing);
  // THE DRAFTS LIVE HERE BECAUSE THE FIELDS DO NOT LIVE TOGETHER. The company's four editable
  // properties are one write to genesis and therefore one draft, but they are rendered in two
  // different places on this page — name, industry and size in the company card, "About us" in its
  // own box beside it — so no component below the page can own the state they share.
  //
  // Called unconditionally, as hooks must be. They hold nothing and announce nothing until the page
  // is actually in edit mode: see CompanyEditingScopes, which is what puts them in the PendingEdits
  // registry and is mounted for exactly as long as the mode is on.
  const profile = useCompanyProfileEditing(company, admin);
  const logo = useCompanyLogoEditing(company?.logo, admin);
  // What `createCompany` will carry, read at call time by the gate. A ref rather than an argument
  // because the two hooks above already take `admin`, and `admin` takes the gate — a value cannot be
  // an argument to the thing that depends on it. Filled from an effect, which is early enough: the
  // only reader is the create button.
  useEffect(() => {
    draftSource.current = { profile: profile.draft, logo: logo.picked };
  }, [profile.draft, logo.picked]);

  // THE ROSTER IS NEVER EMPTY, not even for a company that does not exist yet: genesis writes the
  // creator's own admin membership in the same transaction as the company row. Saying so here is
  // what keeps the create state honest — the creator is not offered to themselves in the people
  // search, they can be marked as an Ansprechperson (genesis only ever marks an EXISTING member, so
  // there is no other way to name yourself one at creation), and the sole-admin guard knows there is
  // one administrator rather than none.
  const selfRoster = useMemo(
    () =>
      me.id
        ? [
            {
              role: COMPANY_ROLE_ADMIN,
              contactPerson: false,
              person: { id: me.id, formattedName: me.fullName },
            },
          ]
        : [],
    [me.id, me.fullName],
  );
  const rosterRows = creating ? selfRoster : members;

  // ONE COMMIT, AND THE ORDER GENESIS REQUIRES. The gate creates the company; every other write on
  // the page then resolves its target through the same latched promise, so the fan-out below is safe
  // to run in parallel — contact-person marks are ordered after the memberships they mark inside the
  // member draft's own apply(), which is where that ordering already lived.
  const pending = usePendingEdits();
  const [flushing, setFlushing] = useState(false);
  const [flushError, setFlushError] = useState<string | null>(null);

  async function createCompany() {
    setFlushing(true);
    setFlushError(null);
    try {
      const created = await creation.ensure();
      if (!created.ok) return;
      setCreatedId(created.id);
      // Landing in EDIT mode is the point — the admin has just been filling this page in and the
      // next thing they do is usually more of it. `editing` is set as well as inferred so the mode
      // toggle agrees with the sections: without it the bar offered "Edit company" over a page that
      // was already editable.
      setEditing(true);
      const scopes = COMPANY_PENDING_SCOPES.map(
        (scope) => pending?.scopes[scope],
      ).filter((scope) => scope != null);
      await Promise.all(scopes.map((scope) => scope.save()));
    } catch (cause) {
      // A refusal from one of the staged lists. The company itself exists by now, so this is never
      // "creation failed": what is left on screen is the company, with the rows that did not land
      // still staged in their editors and the SaveBar offering to write them again.
      //
      // The scope contract is that a deliberate refusal rejects with a LocalizedError carrying
      // product copy, so that message goes straight to the screen — the same rule the unsaved-edits
      // gate follows, and for the same reason: running it back through userErrorMessage would
      // replace an actionable sentence with a generic "something went wrong" (@adr SPARK-0010-no-raw-server-errors-in-ui@1).
      const localized = localizedMessageOf(cause);
      if (!localized) logApplicationFailure("company-create flush");
      setFlushError(localized ?? userErrorMessage(intl, cause));
    } finally {
      setFlushing(false);
    }
  }

  // THE URL CATCHES UP WITHOUT A ROUTE CHANGE, and that is the whole of it.
  //
  // `navigate('/c/:id')` was the obvious move and it was wrong: /c/new and /c/:id are two route
  // entries, so it unmounts this component — taking the member draft, the staged expertise and any
  // refusal with it. On a partial create that is precisely the work @adr SPARK-0038-creating-a-company-is-the-company-page-before-it-exists@2 promises not to lose.
  //
  // Guarding the navigation on "nothing is dirty" cannot fix it. The PendingEdits registry publishes
  // `dirty` from an effect (lib/pending-edits), so it is always one commit behind the editors — and
  // `creating` goes false in the very render that first HAS a company, which is the render whose
  // effect would decide the move. The guard would read the pre-flip `false` on the one commit where
  // it matters, every time, for every refusal.
  //
  // So the address bar is corrected in place instead. Nothing unmounts, nothing is lost, and the
  // question of whether the flush succeeded stops being load-bearing. React Router keeps matching
  // /c/new until something actually navigates, which costs nothing here: every link this page draws
  // is absolute, a reload lands on /c/:id, and the replaced entry is gone from history so Back does
  // not offer an empty create form that would invite a second company.
  const movedUrl = useRef(false);
  useEffect(() => {
    if (movedUrl.current || !createdId || !company) return;
    movedUrl.current = true;
    window.history.replaceState(window.history.state, "", `/c/${createdId}`);
  }, [company, createdId]);

  // Leaving the mode drops whatever was not saved, which is what unmounting the two deleted editor
  // sections used to do. Doing it explicitly rather than by unmounting means the discard is visible
  // in the code that causes it, and that the SaveBar's entry disappears together with the work it
  // was offering to save rather than after it.
  /**
   * Leaves edit mode, dropping whatever was not saved.
   *
   * <p>Called only once the gate below has an answer — never straight from the button. Until the
   * whole edit view became a draft this discarded a couple of typed fields; now it can drop a staged
   * roster, a staged expertise list and a picked logo at once, which is far too much to lose to a
   * click that reads as "I am finished".
   */
  function stopEditing() {
    profile.discard();
    logo.reset();
    setEditing(false);
  }

  if (loading && !registering) {
    return (
      <main
        className="w-full max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10"
        data-graphql="CompanyProfile"
      >
        <div className="mb-5">
          <BackButton />
        </div>
        <p className="smycles-field-meta text-ink-muted">
          <FormattedMessage
            id="company.page.loading"
            defaultMessage="Loading company"
          />
        </p>
      </main>
    );
  }

  // genesis answers null for an unknown id and for one this caller may not read alike, so a single
  // message has to cover both — claiming "does not exist" would be a guess, and claiming "no access"
  // would leak that it exists.
  //
  // `error` is a GENUINE failure here and never the "no such company" answer: useCompany keeps
  // genesis's NoSuchEntity/InvalidIdentifier out of it, because a mistyped /c/:id arrives as an
  // error and would otherwise be reported with a support code. Nothing went wrong, and a support
  // code spent on a typo teaches the reader to ignore the next one.
  if (notFound && !registering) {
    return (
      <main
        className="w-full max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10"
        data-graphql="CompanyProfile"
      >
        <div className="mb-5">
          <BackButton />
        </div>
        <NoBackend
          id="company-notfound"
          label={intl.formatMessage({
            id: "company.page.notFound",
            defaultMessage: "Company not found",
          })}
          hint={
            error
              ? userErrorMessage(intl, error)
              : intl.formatMessage({
                  id: "company.page.notFoundHint",
                  defaultMessage:
                    "This company does not exist or is not visible to you.",
                })
          }
        />
      </main>
    );
  }

  if (!company && !registering) return null;

  return (
    <main
      className="w-full max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10"
      data-graphql="CompanyProfile"
    >
      {!registering && (
        <div className="mb-5">
          <BackButton />
        </div>
      )}
      {!registering && company && me.id && <div id="company-comparison-bar" className="mb-6 flex flex-wrap items-center gap-3">
        <ComparisonTools comparable={companyComparable} viewerId={me.id} current={company} />
        <ComparisonSelection comparable={companyComparable} viewerId={me.id} current={company} />
      </div>}
      {/* Asked only when something is actually unsaved; otherwise the switch happens on the click,
          as it did before. Same dialog and same words as the one the app shell's bar shows when the
          context is LEFT — one occasion, two controls. */}
      <UnsavedChangesDialog
        open={contextGate.blocked}
        body={
          <FormattedMessage
            id="company.context.unsavedBody"
            defaultMessage="Changing the company context reloads everything on screen. Changes you have not saved will be lost."
          />
        }
        saving={contextGate.saving}
        error={contextGate.error}
        onStay={contextGate.stay}
        onDiscard={contextGate.discardAndProceed}
        onSave={contextGate.saveAndProceed}
      />

      {/* The same three answers for the other way of losing an edit: Done editing. Its own wording,
          because "everything on screen reloads" is not what happens here — the mode simply ends. */}
      <UnsavedChangesDialog
        id="company-editgate"
        open={editingGate.blocked}
        body={
          <FormattedMessage
            id="company.page.stopEditingUnsavedBody"
            defaultMessage="Leaving edit mode discards everything you have not saved: the profile, the logo, and any people or skills you added or removed."
          />
        }
        saving={editingGate.saving}
        error={editingGate.error}
        onStay={editingGate.stay}
        onDiscard={editingGate.discardAndProceed}
        onSave={editingGate.saveAndProceed}
      />

      {/* THE LOGO IS FRAMED BEFORE IT IS STAGED, by the very cropper a person's photo goes through
          (Kevin, 2026-08-28). Here beside the page's other dialogs rather than inside the identity
          card: it is a full-screen modal, so nesting it in the card would put its markup inside the
          one box every reader — and every test — treats as the company's own four facts.
          `cropFile` is only ever set by the control in that card, so this stays absent for a visitor
          and for an admin who is not editing, without a second condition saying so. */}
      {logo.cropFile ? (
        <AvatarCropper
          file={logo.cropFile}
          onCancel={logo.cancelCrop}
          onApply={logo.applyCrop}
          onInvalid={logo.rejectCrop}
        />
      ) : null}

      {/* WHAT THE REST OF THE APP IS TOLD ABOUT AN OPEN EDIT. Mounted for exactly as long as the
          mode is on, which is the whole trick: a hook cannot be called conditionally, but a
          component can be rendered conditionally, and unmounting is what takes the entries back out
          of the registry when editing stops. */}
      {isEditing ? (
        <CompanyEditingScopes
          profile={profile}
          logo={logo}
          creating={creating}
        />
      ) : null}

      {/* TWO COLUMNS, AND THEY ARE THE PERSON PAGE'S (@adr SPARK-0038-creating-a-company-is-the-company-page-before-it-exists@2). A company page answers the same
          question a person page answers — who is this, what can they do, who is behind them — so it
          takes the same grid rather than inventing a second one: identity in a rail, everything the
          company DOES beside it. The single tall stack this replaced made a company with three
          filled sections a scrolling exercise, and left the logo floating over the page as loose
          furniture with nothing to belong to. */}
      <div className="grid grid-cols-1 items-start gap-6 @lg:grid-cols-[minmax(280px,0.5fr)_minmax(0,1fr)]">
        <aside className="flex flex-col gap-4">
          {/* In edit mode the identity IS the form — see CompanyIdentity. The two sections that used
              to redraw these facts as labelled inputs below the page are gone, and so is the
              registration form that redrew them a third time. */}
          <CompanyIdentity
            company={company ?? { name: profile.draft.name }}
            profile={isEditing ? profile : null}
            logo={isEditing ? logo : null}
            busy={admin.busy}
          />

          {/* About us, edited where it is read. There is no "edit the About us text" field anywhere
            else on this page any more: this box IS the field, and in view mode it is the paragraph
            it always was.

            The placeholder an admin clicks on is the same sentence a visitor would have read for a
            company that has described itself yet — one string, doing the two jobs it can honestly
            do: stating the absence to a reader, and standing where the text will go for an admin.
            In view mode it is a note; in edit mode it is the empty field. */}
          <Section
            title={
              <FormattedMessage
                id="company.page.about.title"
                defaultMessage="About us"
              />
            }
          >
            {isEditing ? (
              <InlineEdit
                id="company-summary"
                ariaLabel={intl.formatMessage({
                  id: "company.admin.field.summary",
                  defaultMessage: "About us",
                })}
                value={profile.draft.summary}
                onChange={(next) => profile.set("summary", next)}
                editable={!admin.busy}
                multiline
                placeholder={intl.formatMessage({
                  id: "company.page.about.empty",
                  defaultMessage:
                    "This company has not described itself yet.",
                })}
                className="block w-full"
                inputClassName="smycles-field-value leading-relaxed text-ink"
              />
            ) : company?.summary ? (
              <p id="company-summary" className="whitespace-pre-line break-words text-smycles-body text-ink">
                {company.summary}
              </p>
            ) : (
              <p id="company-summary" className="smycles-field-meta text-ink-muted">
                <FormattedMessage
                  id="company.page.about.empty"
                  defaultMessage="This company has not described itself yet."
                />
              </p>
            )}
          </Section>

            {/* Ansprechpartner:innen IN THE RAIL, under About us. "Who do I talk to" is a fact about
              who this company IS, not about what it does, so it belongs beside the card and the
              company's own words rather than in the column of its output — and the rail had the room,
              standing empty below the buttons while the column ran on for another screen.

              It used to sit in the column above the roster, so that the same face appearing twice
              read as "talk to these two, and here is everyone". That adjacency stopped existing the
              moment Beiträge und Projekte moved between them, which is the whole of the argument for
              leaving it there.

              Rendered for EVERY viewer, admin or not — it is part of the showcase, and the flagging
              control that creates it lives in the member editor below, not here.

              Nothing at all when none are marked: no heading, no frame, no "none yet" line. The block
              is an answer to "who do I talk to", and a company that has not named anybody has no
              answer to show.

              Its own list shape rather than the strip's pills: it is a different question, so reusing
              the pill would read as the same row twice instead of as a distinct block. And it is not
              true that everybody here also appears in the strip below — that strip shows a page, and a
              contact person can be on a later one — which is why this block reads the complete set
              from genesis rather than filtering what the strip happens to hold.
              Neutral throughout — no accent ring and no coloured border; the heading and the note
              carry the meaning. */}
            {/* HIDDEN WHILE EDITING, because in that mode it is a second, STALE answer. The marks
                are staged in the member editor and only reach genesis on save, while this block
                reads the stored set straight from the query — so an admin who has just marked
                somebody sees them marked there and absent here, in a view whose whole premise is
                that it shows what Save will do. The editor already lists every member with their
                mark, so nothing is lost by standing down; lifting the draft up to this page to keep
                both in step would be the larger change, for a block that is redundant in this mode
                anyway. */}
            {!isEditing && contactPersons.length > 0 ? (
              <Section
                title={
                  <FormattedMessage
                    id="company.page.contacts.title"
                    defaultMessage="Contact persons"
                  />
                }
              >
                <p className="smycles-field-meta mb-3 text-ink-muted">
                  <FormattedMessage
                    id="company.page.contacts.hint"
                    defaultMessage="The people this company named to talk to. Open a profile to get in touch."
                  />
                </p>
                {/* Named, unlike the member strip above it. Every one of these people also appears in
                  that strip, so a screen reader meeting the second copy of "Alan Turing" needs the
                  list itself to say which question it is answering. */}
                <ul
                  aria-label={intl.formatMessage({
                    id: "company.page.contacts.title",
                    defaultMessage: "Contact persons",
                  })}
                  /* Pills that wrap, the same shape as the member strip — a capped handful of these
                   (0006 §4), and one full-width row each spent a whole line on a name and an avatar.
                   The strip already proves the shape reads well for the same people. */
                  className="flex flex-wrap gap-3"
                >
                  {contactPersons.map((contact) => (
                    <li key={contact.person.id}>
                      {/* The same pill the roster draws, real avatar and all. This was once the
                          one place on the page that had a face, because the cap bounded the bytes;
                          the roster's own page bounds them too. */}
                      <CompanyPersonPill
                        id={contact.person.id}
                        idPrefix="company-contact"
                        name={contact.person.formattedName}
                        avatarContent={contact.person.avatar?.content}
                        avatarMime={contact.person.avatar?.mimeType}
                      />
                    </li>
                  ))}
                </ul>
              </Section>
            ) : null}

          {/* THE PAGE'S ONE ACTION WHILE THE COMPANY IS STILL HYPOTHETICAL, and the second of the
              two commit points @adr SPARK-0038-creating-a-company-is-the-company-page-before-it-exists@2 keeps: an explicit "Create company" here, the SaveBar
              afterwards. They are never on screen together — nothing reports itself dirty while the
              create state is on, so the bar has nothing to appear for. */}
          {creating && createdId ? (
            // CREATED, AND WAITING FOR THE READ THAT ENDS THE CREATE STATE. Offering the button
            // again here is how a page invites a second company: the flush has already run, and a
            // read that never lands would otherwise leave "Create company" sitting over a company
            // that already exists. The same line every other unloaded company page shows.
            <p className="smycles-field-meta text-ink-muted">
              <FormattedMessage id="company.page.loading" defaultMessage="Loading company" />
            </p>
          ) : creating ? (
            <div className="flex flex-col gap-2">
              <Button
                id="company-create"
                size="compact"
                disabled={flushing || creation.busy}
                onClick={() => void createCompany()}
              >
                <FormattedMessage
                  id="company.create.submit"
                  defaultMessage="Create company"
                />
              </Button>
              {creation.message ? (
                <p id="company-create-message" className="smycles-field-meta text-ink">{creation.message}</p>
              ) : null}
            </div>
          ) : /* The Kontext-Switch-Button of The company page, "für
                 Admins der Firma" — so it is gated on `viewerIsAdmin`, the membership row, exactly
                 like the inline controls beside it. That gate is presentation only; entering the
                 mode grants nothing, and genesis re-checks the row on every write.

                 Only ever ENTERS from here. Leaving is one control in one place, the bar in the app
                 shell, which is on screen for the whole time the mode is on — a second exit sitting
                 on one company's page would be the odd one out the moment the person navigated
                 away. */
          viewerIsAdmin ? (
            <div className="flex flex-col gap-2">
              {/* EDITING IS AN ACTION ON THE COMPANY, so it stands with the company rather than
                  across the top of the sections it happens to reveal. The strip it used to live in
                  carried a sentence explaining that an administrator may edit the profile, the
                  expertise and the members — which is what a button labelled "Edit company" already
                  says, to the only person who can see it. */}
              <Button
                id="company-edit-toggle"
                variant="toggle"
                size="compact"
                aria-pressed={editing}
                // Through the gate on the way OUT only: entering edit mode destroys nothing.
                onClick={() =>
                  editing ? editingGate.request(stopEditing) : setEditing(true)
                }
              >
                {editing ? (
                  <FormattedMessage
                    id="company.admin.done"
                    defaultMessage="Done editing"
                  />
                ) : (
                  <FormattedMessage
                    id="company.admin.edit"
                    defaultMessage="Edit company"
                  />
                )}
              </Button>
              {!actingAsThisCompany ? (
                <Button
                  id="company-act-as"
                  variant="chip"
                  size="compact"
                  onClick={() =>
                    contextGate.request(() => companyContext.enter(id))
                  }
                >
                  <FormattedMessage
                    id="company.context.enter"
                    defaultMessage="Act as this company"
                  />
                </Button>
              ) : null}
              {/* LAST IN THE BLOCK. This rail is "what you may do to this company" (@adr SPARK-0038-creating-a-company-is-the-company-page-before-it-exists@2), and
                  the lifecycle act belongs at the bottom of it — under the two everyday ones, not
                  competing with them.

                  NO `!creating` GUARD, deliberately: the create state is already answered two arms
                  up in this same ternary, so a second check here would be dead by construction —
                  which is exactly what it was, and coverage said so. There is nothing to deactivate
                  before genesis has minted an id, and a company is born ACTIVE (`status` is not
                  creatable); the chain above is what enforces that.

                  THE `company ?` THAT REMAINS IS THE TYPE NARROWING, not a runtime case, and its
                  null arm is the one line here coverage cannot reach. `company` is `CompanyData |
                  null` at this point and neither early return above narrows it, so reading
                  `company.status` needs the check; every path that actually reaches this arm has a
                  loaded company. Same shape, and the same reason, as the guard in
                  CompanyMemberEditor#stagePicked.

                  It writes on the click rather than through the SaveBar — see CompanyStatusControl
                  for why the concept puts it outside the draft. */}
              {company ? (
                <CompanyStatusControl
                  status={company.status}
                  busy={admin.busy}
                  onChange={async (next) => {
                    const result = await admin.setStatus(next);
                    return result.ok ? null : result.message;
                  }}
                />
              ) : null}
              {/* Whether the last save landed, for the fields this rail holds. One write, so one
                  answer, rather than a line beside each of the four values it covers. */}
              {isEditing && profile.message ? (
                <p id="company-profilemessage" className="smycles-field-meta text-ink">{profile.message}</p>
              ) : null}
              {isEditing && profile.saved && !profile.message ? (
                <p id="company-saved" className="smycles-field-meta text-ink-muted">
                  <FormattedMessage
                    id="company.admin.profile.saved"
                    defaultMessage="Saved."
                  />
                </p>
              ) : null}
            </div>
          ) : null}

          {/* Beside the admin actions, not among them: reading the file is a capability of the
              viewer, not a role in this company. */}
          {organizationalRecordAccess.canRead && organizationalRecordSubjectId ? (
            <div className="flex flex-col gap-2">
              <OrganizationalRecordToggle expanded={organizationalRecordExpanded} onOpen={openOrganizationalRecord} />
            </div>
          ) : null}

          {/* The company exists by the time this can be non-null, so it never reads as "creation
              failed": it names the staged rows that did not land, next to the page that still holds
              them. */}
          {flushError ? (
            <p className="smycles-field-meta text-ink">{flushError}</p>
          ) : null}
        </aside>

        <div className="flex flex-col gap-6">
          {/* expertise leads: the company capability model makes this the match-relevant core and
            everything below it descriptive context. */}
          {/* A READING MEASURE ON THE TEXT, NOT ON THE BOX. An expertise label and its level are a
            line of text, and a line that runs the whole page is read by nobody
            (ive-design-principles: ~50-75 characters) — but narrowing the box instead would give
            this column a ragged right edge, with two boxes ending short of the two below them.
            Written as `max-w-[42rem]` rather than `max-w-2xl` on purpose: see @adr SPARK-0038-creating-a-company-is-the-company-page-before-it-exists@2, index.css
            redefines the container tokens `max-w-*` reads and `max-w-2xl` is 96rem here. */}
          <Section
            title={
              <FormattedMessage
                id="company.page.competencies.title"
                defaultMessage="Skills"
              />
            }
          >
            <div className="max-w-[42rem]">
              {isEditing ? (
                <CompanyCompetencyEditor
                  competencies={competencies}
                  admin={admin}
                  creating={creating}
                />
              ) : (
                <CompanyCompetencies competencies={competencies} />
              )}
            </div>
          </Section>

          {/* The company's work, ABOVE its roster. Posts and projects are the company showing rather
              than telling — the evidence behind the expertise two boxes up — while the People strip
              is a directory whose headline number the card already states. Identity, then what they
              can do, then who to talk to, then what they have done, then who is here.

              AND NOTHING AT ALL WHEN THERE IS NOTHING, the same rule the contact block above
              follows. Most companies have shared nothing, and moving the block up while it still
              said "Nothing has been shared yet." would put that sentence above the people for
              almost everybody — louder than the note deserves, and worse than where it used to sit.
              A heading promising work a company has not done is worse than its absence. */}
          {sharedItems.length > 0 ? (
            <Section
              id="company-work"
              title={
                <FormattedMessage
                  id="company.page.work.title"
                  defaultMessage="Posts and projects"
                />
              }
            >
              {(
              /* The same tile the home page and the extended search draw, rather than a list of
               links only this page knows how to render — a company's work should look like work
               wherever it appears. `hideCompany`, because every tile here belongs to the company
               whose page this is and repeating its name on each would say nothing. */
                <ul className="flex flex-wrap gap-3">
                  {sharedItems.map((item, i) => (
                    <li key={item.id} className="w-56 @sm:w-64">
                      <Link
                        id={`company-workitem-${item.id}`}
                        to={`/share/discover/${item.id}`}
                        className="block h-full transition hover:-translate-y-0.5"
                      >
                        <ShareItemCard item={item} index={i} hideCompany />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          ) : null}

          {/* THE SECTION IS ABSENT, NOT EMPTY, WHEN THE ROSTER IS WITHHELD — concept v25,
              the company page.
              genesis refuses `companyMembers` to a caller who does not read INTERNAL, and `members`
              is then `[]` — which the branch below would render as "Nobody is listed yet.", a plain
              lie: people ARE listed, this reader may not see them. Dropping the whole section says
              the true thing, and says it the way v25 asked for — the workforce is not showcase
              content, so nothing here announces what is being withheld. The contact strip (§4) is a
              separate Section and stays exactly as it is for this reader. */}
          {rosterWithheld ? null : (
            <Section
              title={
                <FormattedMessage
                  id="company.page.members.title"
                  defaultMessage="People"
                />
              }
            >
              {isEditing ? (
                <CompanyMemberEditor
                  members={rosterRows}
                  contactPersons={creating ? [] : contactPersons}
                  rosterComplete={creating || !hasMoreMembers}
                  admin={admin}
                  creating={creating}
                  count={creating ? rosterRows.length : memberCount}
                  hasMore={!creating && hasMoreMembers}
                  loadingMore={loadingMoreMembers}
                  moreError={moreMembersError}
                  onLoadMore={loadMoreMembers}
                />
              ) : members.length === 0 ? (
                <p className="smycles-field-meta text-ink-muted">
                  <FormattedMessage
                    id="company.page.members.empty"
                    defaultMessage="Nobody is listed yet."
                  />
                </p>
              ) : (
                <>
                  {/* The "Klick" of §2: every pill is a link to the person's real profile at /p/:id. */}
                  <ul className="flex flex-wrap gap-3">
                    {members.map((member) => (
                      <li key={member.person.id}>
                        <CompanyPersonPill
                          id={member.person.id}
                          idPrefix="company-person"
                          name={member.person.formattedName}
                          avatarContent={member.person.avatar?.content}
                          avatarMime={member.person.avatar?.mimeType}
                        />
                      </li>
                    ))}
                  </ul>
                  <CompanyMemberCount
                    shown={members.length}
                    total={memberCount}
                    hasMore={hasMoreMembers}
                    loading={loadingMoreMembers}
                    error={moreMembersError}
                    onLoadMore={loadMoreMembers}
                  />
                </>
              )}
            </Section>
          )}
        </div>
      </div>
      {organizationalRecordSubjectId ? (
        <OrganizationalRecordSection
          expanded={organizationalRecordExpanded}
          sectionRef={organizationalRecordSectionRef}
          subject={{ kind: "company", id: organizationalRecordSubjectId }}
          access={organizationalRecordAccess}
        />
      ) : null}
    </main>
  );
}

/**
 * The two page-wide PendingEdits entries, registered for exactly as long as /c/:id is in edit mode.
 *
 * A COMPONENT RATHER THAN TWO MORE LINES IN THE PAGE, and not for tidiness. `usePendingScope` is a
 * hook and hooks cannot be called conditionally, but the drafts it announces have to live above the
 * fields — which are scattered across the header and the About us box — so the page is the only
 * place that can hold them. Rendering the registration instead of calling it is what lets the two
 * facts coexist: the state is page-level, the entries come and go with the mode.
 *
 * AND THE ENTRIES ARE THE ONLY SAVE. Neither the identity header nor the About us box carries a
 * Save or a Discard of its own: a reviewer had those removed from the deleted sections because two
 * controls for one operation make the reader guess which is the real one. The SaveBar the app shell
 * renders reads exactly these entries, and so do UnsavedGuard and the acting-company gate.
 *
 * TWO SCOPES, NOT ONE, because they are two writes with two refusals: genesis reads an update off
 * the properties the request carried, so the logo travels on its own request (see use-company-admin)
 * and a profile save that genesis refuses must not report the logo as unsaved.
 */
function CompanyEditingScopes({
  profile,
  logo,
  creating,
}: {
  profile: CompanyProfileEditing;
  logo: CompanyLogoEditing;
  /** See the note on `dirty` below. */
  creating: boolean;
}) {
  // REGISTERED, BUT NEVER DIRTY WHILE THE COMPANY IS HYPOTHETICAL — and both halves matter.
  //
  // Registered, because the create button reaches these drafts through the registry: it is the only
  // seam that gets from the page to state the editors own.
  //
  // Never dirty, because `dirty` is what the SaveBar and UnsavedGuard read, and neither belongs to
  // the create state. The bar would stand beside "Create company" offering the same commit under a
  // second name, and the guard would defend a form that has nothing to defend — which is exactly the
  // judgement the deleted /c/new made when it registered no scope at all: "there is nothing here for
  // the unsaved-changes machinery to protect."
  usePendingScope(COMPANY_SCOPE_PROFILE, {
    dirty: !creating && profile.dirty,
    save: profile.saveOrThrow,
    discard: profile.discard,
  });
  usePendingScope(COMPANY_SCOPE_LOGO, {
    dirty: !creating && logo.unsaved,
    save: logo.saveOrThrow,
    discard: logo.discard,
  });
  return null;
}
